import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { FaceDetailsInfo } from "./faceDetailsSlice";
import { requestSetFaceSomebody } from "./faceSetSomebodySlice";
import { requestSetFaceNobody } from "./faceSetNobodySlice";

export interface FacesDetailsListState {
    state: LoadStateInfo;
    request?: RetrieveFaceDetailsListRequest;
    list?: FaceDetailsInfo[];
}

const initialState: FacesDetailsListState = {
    state: notStarted(),
};

export interface RetrieveFaceDetailsListRequest {
    imageId: string;
}

export const retrieveFaceDetailsList = createAsyncThunk(
    "retrieveFaceDetailsList",
    async (params: RetrieveFaceDetailsListRequest, thunkAPI) => {
        console.debug("retrieveFaceDetailsList");

        const response = await fetch(`${getBackendUrl()}/api/face/${params.imageId}/list`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
                "Content-Type": "application/json",
            },
            method: "GET",
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);


export const faceDetailsListSlice = createSlice({
    name: "faceDetailsListSlice",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        check: (state, action: PayloadAction<RetrieveFaceDetailsListRequest>) => {
            if (state.request?.imageId != action.payload.imageId) {
                state.state = notStarted();
                state.request = action.payload;
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveFaceDetailsList.pending, (state, /* action */) => {
                state.state = loading();
                state.list = undefined;
            })
            .addCase(retrieveFaceDetailsList.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                state.list?.sort((a, b) => {
                    const la: number[] = a.photoFace.location.split("_").map((item) => parseInt(item));
                    const lb: number[] = b.photoFace.location.split("_").map((item) => parseInt(item));
                    const y = Math.round((la[1] - lb[1]) / 150);
                    const x = Math.round((la[0] - lb[0]) / 150);
                    if (y != 0)
                        return y;
                    return x;
                });
            })
            .addCase(retrieveFaceDetailsList.rejected, (state, action) => {
                state.state = error(action);
            })

            .addCase(requestSetFaceSomebody.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                const received = action.payload as FaceDetailsInfo;
                const found = state.list?.find((item) => item.photoFace.id == received.photoFace.id);
                if (found) {
                    found.photoFace = received.photoFace;
                    found.student = received.student;
                    found.user = received.user;
                }
            })

            .addCase(requestSetFaceNobody.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                const received = action.payload as FaceDetailsInfo;
                const found = state.list?.find((item) => item.photoFace.id == received.photoFace.id);
                if (found) {
                    found.photoFace = received.photoFace;
                    found.student = received.student;
                    found.user = received.user;
                }
            })
            
            ;
    },
});

// export const { } = faceDetailsListSlice.actions;

export const selectFaceDetailsList = (state: RootState) => state.faceDetailsList;

export default faceDetailsListSlice.reducer;
